import { AppImageClient } from '@components/appImage';
import { Button } from '@components/button';
import { Card } from '@components/card';
import { ParseWithTooltip } from '@components/parseWithTooltip';
import { Sanitizer } from '@components/sanitizer';
import { classNames } from '@lib/classNames/classNames';

import type { RoutingMedia } from '../constants';
import { FeatureCard } from '../types';

interface Props {
  card: FeatureCard;
  /** Медиа для dynamic-карточек: AppImage-картинка или публичный gif */
  media?: RoutingMedia;
  forceMobileLayout?: boolean; // принудительно мобильный layout
}

export const RoutingFeatureCard = ({
  card,
  media,
  forceMobileLayout = false,
}: Props) => {
  const renderImage = (className: string) => {
    if (media?.type === 'image') {
      return (
        <AppImageClient
          {...media.data}
          alt={card.imageAlt}
          className={className}
        />
      );
    }

    const src =
      media?.src ?? (card.imageUrl !== 'dynamic' ? card.imageUrl : undefined);

    return <img src={src} alt={card.imageAlt} className={className} />;
  };

  // Компонент для текстовой части карточки (desktop)
  const TextContent = () => (
    <div className="flex flex-col justify-between p-[28px]">
      <div className="flex flex-col gap-3">
        <h4 className="text-[30px] leading-[38px] font-medium tracking-[-0.45px]">
          {card.title}
        </h4>
        <div className="text-[16px] leading-[24px] font-normal tracking-[-0.16px]">
          {card.useTooltip ? (
            <ParseWithTooltip
              position="top-start"
              text={card.description}
              tooltipClassName="max-w-[360px]"
              tooltipContent="ETA (Estimated Time of Arrival, расчётное время прибытия) — это прогнозируемое время прибытия в пункт назначения."
            />
          ) : (
            <p>{card.description}</p>
          )}
        </div>
      </div>
      <Button
        target="_blank"
        variant="secondary"
        className="size-fit px-4 py-4"
        href={card.buttonHref}
      >
        {card.buttonText}
      </Button>
    </div>
  );

  // Мобильная версия: всегда вертикальный layout
  if (forceMobileLayout) {
    return (
      <Card className="flex h-auto flex-col overflow-hidden rounded-[20px] p-2">
        {renderImage('h-[240px] w-full rounded-[12px] object-cover')}
        <div className="flex flex-col gap-3 p-3 pt-4">
          <h4 className="text-[20px] leading-[24px] font-medium tracking-[-0.1px]">
            {card.title}
          </h4>
          <div className="text-[16px] leading-[24px] font-normal tracking-[-0.16px]">
            <Sanitizer htmlContent={card.description} />
          </div>
          <Button
            target="_blank"
            variant="secondary"
            className="mt-1 w-full items-center justify-center px-4 py-3"
            href={card.buttonHref}
          >
            {card.buttonText}
          </Button>
        </div>
      </Card>
    );
  }

  // Desktop: горизонтальный layout
  if (
    card.layout === 'horizontal-image-right' ||
    card.layout === 'horizontal-image-left'
  ) {
    const imageOnRight = card.layout === 'horizontal-image-right';

    return (
      <Card
        className={classNames({
          cls: 'p-2',
          mods: { [card.className || '']: !!card.className },
        })}
      >
        <div className="flex h-full">
          {imageOnRight && <TextContent />}

          {renderImage(
            'size-full h-auto max-w-[560px] shrink-0 rounded-[12px] object-cover',
          )}

          {!imageOnRight && <TextContent />}
        </div>
      </Card>
    );
  }

  // Desktop: вертикальный layout
  return (
    <Card className={card.className}>
      <div className="flex h-full flex-col p-2">
        {renderImage('h-[312px] w-full rounded-[12px] object-cover')}
        <div className="flex h-full flex-col justify-between px-5 pt-6 pb-8">
          <div className="flex flex-col gap-3">
            <h4 className="text-[30px] leading-[38px] font-medium tracking-[-0.45px]">
              {card.title}
            </h4>
            <p className="text-[16px] leading-[24px] font-normal tracking-[-0.16px]">
              {card.description}
            </p>
          </div>
          <Button
            target="_blank"
            variant="secondary"
            className="size-fit px-4 py-4"
            href={card.buttonHref}
          >
            {card.buttonText}
          </Button>
        </div>
      </div>
    </Card>
  );
};
